// Service Worker 管理サービス
// キャッシュ戦略とオフライン対応

interface ServiceWorkerConfig {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private isOnline: boolean = navigator.onLine;
  private config: ServiceWorkerConfig = {};

  constructor(config: ServiceWorkerConfig = {}) {
    this.config = config;
    this.setupNetworkListeners();
  }

  // Service Workerの登録
  public async register(swUrl: string = '/sw.js'): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker はサポートされていません');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.register(swUrl);
      this.registration = registration;

      console.log('Service Worker 登録成功:', registration);

      // 更新の確認
      registration.addEventListener('updatefound', () => {
        this.handleUpdateFound(registration);
      });

      // すでにアクティブなService Workerがある場合
      if (registration.active) {
        console.log('Service Worker がアクティブです');
        this.config.onSuccess?.(registration);
      }

      // 定期的な更新チェック
      this.scheduleUpdateCheck();

      return true;

    } catch (error) {
      console.error('Service Worker 登録失敗:', error);
      return false;
    }
  }

  // Service Worker更新の処理
  private handleUpdateFound(registration: ServiceWorkerRegistration) {
    const installingWorker = registration.installing;
    
    if (!installingWorker) return;

    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed') {
        if (navigator.serviceWorker.controller) {
          // 新しいService Workerが利用可能
          console.log('新しいService Worker が利用可能です');
          this.config.onUpdate?.(registration);
        } else {
          // 初回インストール完了
          console.log('Service Worker インストール完了');
          this.config.onSuccess?.(registration);
        }
      }
    });
  }

  // ネットワーク状態の監視
  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      console.log('オンラインになりました');
      this.isOnline = true;
      this.config.onOnline?.();
    });

    window.addEventListener('offline', () => {
      console.log('オフラインになりました');
      this.isOnline = false;
      this.config.onOffline?.();
    });
  }

  // 新しいService Workerをスキップして有効化
  public skipWaiting(): void {
    if (this.registration?.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  // Service Workerのバージョンを取得
  public async getVersion(): Promise<string | null> {
    return new Promise((resolve) => {
      if (!this.registration?.active) {
        resolve(null);
        return;
      }

      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(event.data.version || null);
      };

      this.registration.active.postMessage(
        { type: 'GET_VERSION' },
        [channel.port2]
      );
    });
  }

  // キャッシュをクリア
  public async clearCache(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.registration?.active) {
        resolve(false);
        return;
      }

      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(event.data.success || false);
      };

      this.registration.active.postMessage(
        { type: 'CLEAR_CACHE' },
        [channel.port2]
      );
    });
  }

  // 定期的な更新チェック
  private scheduleUpdateCheck() {
    // 10分ごとに更新をチェック
    setInterval(() => {
      if (this.registration) {
        this.registration.update().catch((error) => {
          console.warn('Service Worker 更新チェック失敗:', error);
        });
      }
    }, 10 * 60 * 1000);
  }

  // オンライン状態の取得
  public getOnlineStatus(): boolean {
    return this.isOnline;
  }

  // Service Worker登録の取得
  public getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }

  // Service Workerの登録解除
  public async unregister(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      await this.registration.unregister();
      console.log('Service Worker 登録解除完了');
      return true;
    } catch (error) {
      console.error('Service Worker 登録解除失敗:', error);
      return false;
    }
  }
}

// シングルトンインスタンス
export const serviceWorkerManager = new ServiceWorkerManager();

// React Hook用のエクスポート
export { ServiceWorkerManager };
export type { ServiceWorkerConfig };

// パフォーマンス監視のためのメトリクス収集
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private _clsDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor() {}

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // ページロード時間の測定
  public measurePageLoad(): void {
    if (!('performance' in window)) return;

    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      const metrics = {
        dns: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcp: navigation.connectEnd - navigation.connectStart,
        request: navigation.responseStart - navigation.requestStart,
        response: navigation.responseEnd - navigation.responseStart,
        domParsing: navigation.domInteractive - navigation.responseEnd,
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        totalPageLoad: navigation.loadEventEnd - navigation.startTime
      };

      // メトリクスをアナリティクスに送信（実装時に詳細化）
      // console.logはsendMetrics内で開発環境のみ出力
      this.sendMetrics('page_load', metrics);
    });
  }

  // リソース読み込み時間の測定
  public measureResourceLoad(): void {
    if (!('PerformanceObserver' in window)) return;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          const resource = entry as PerformanceResourceTiming;

          // 非常に大きなリソースまたは読み込み時間が長いリソースのみ記録（閾値を上げてスパム防止）
          if (resource.transferSize > 500000 || resource.duration > 3000) {
            const metrics = {
              name: resource.name,
              size: resource.transferSize,
              duration: resource.duration,
              cacheHit: resource.transferSize === 0 && resource.duration < 50
            };

            // console.logはsendMetrics内で開発環境のみ出力
            this.sendMetrics('resource_load', metrics);
          }
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });
  }

  // Core Web Vitals の測定
  public measureWebVitals(): void {
    // LCP (Largest Contentful Paint)
    this.measureLCP();
    
    // FID (First Input Delay)
    this.measureFID();
    
    // CLS (Cumulative Layout Shift)
    this.measureCLS();
  }

  private measureLCP(): void {
    if (!('PerformanceObserver' in window)) return;

    let lcpReported = false;

    const observer = new PerformanceObserver((list) => {
      // LCPは複数回発火するが、最後の値のみを1回だけ報告
      // ページロード完了後に最終値を報告するためデバウンス
      if (lcpReported) return;

      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];

      // 3秒後に最終値を報告（ページロード完了を想定）
      setTimeout(() => {
        if (!lcpReported) {
          lcpReported = true;
          this.sendMetrics('lcp', { value: lastEntry.startTime });
        }
      }, 3000);
    });

    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  }

  private measureFID(): void {
    if (!('PerformanceObserver' in window)) return;

    let fidReported = false;

    const observer = new PerformanceObserver((list) => {
      // FIDは最初の入力のみ計測するため、1回だけ報告
      if (fidReported) return;

      for (const entry of list.getEntries()) {
        const fid = (entry as any).processingStart ? (entry as any).processingStart - entry.startTime : 0;
        fidReported = true;
        this.sendMetrics('fid', { value: fid });
        break;  // 最初の入力のみ
      }
    });

    observer.observe({ entryTypes: ['first-input'] });
  }

  private measureCLS(): void {
    if (!('PerformanceObserver' in window)) return;

    let clsValue = 0;
    let clsReported = false;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }

      // layout-shift イベントは頻繁に発生するため、デバウンスで最終値のみ報告
      // 5秒間値が変わらなかったら報告（コンソールスパム防止）
      if (this._clsDebounceTimer) {
        clearTimeout(this._clsDebounceTimer);
      }
      this._clsDebounceTimer = setTimeout(() => {
        // CLSは一度だけ報告（または値が大きく変化した場合のみ）
        if (!clsReported || clsValue > 0.1) {
          clsReported = true;
          this.sendMetrics('cls', { value: clsValue });
        }
      }, 5000);
    });

    observer.observe({ entryTypes: ['layout-shift'] });
  }

  // メトリクスの送信（実装はプロジェクトに応じて調整）
  private sendMetrics(type: string, metrics: Record<string, unknown>): void {
    // 開発環境では console.log のみ
    if (import.meta.env.DEV) {
      console.log(`[Performance] ${type}:`, metrics);
      return;
    }

    // 本番環境では実際のアナリティクスサービスに送信
    // 例: Google Analytics, 独自のメトリクスサービスなど
    try {
      // fetch('/api/metrics', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ type, metrics, timestamp: Date.now() })
      // });
    } catch (error) {
      console.warn('メトリクス送信失敗:', error);
    }
  }
}

// パフォーマンス監視のシングルトンインスタンス
export const performanceMonitor = PerformanceMonitor.getInstance();